/* eslint-disable react/prop-types */
/**
 * ContextualCustomerNoteSection
 * =============================
 * Collapsible "Contextual Customer Note" block rendered inside each
 * pricing job_group card.
 *
 * Purpose
 *   Front-of-House can attach ONE customer-facing contextual note per
 *   job_group to communicate workshop nuance (unusual repairs, repair
 *   risk, monitoring advice, cost-saving alternatives, etc.) that the
 *   deterministic explanation engine cannot. The AI rewrite is heavily
 *   constrained (see /app/backend/services/yestimate/contextual_note_service.py).
 *
 * Lifecycle
 *   1. FOH expands the section, picks a note type + rewrite mode,
 *      types raw shorthand, clicks Generate.
 *   2. We POST to /api/foh/yestimate/{yid}/job-group/{jgid}/contextual-note/generate
 *      and render a side-by-side preview (raw on the left, generated on
 *      the right).
 *   3. FOH approves (final_text := generated_text, enabled := true) or
 *      manually edits, or regenerates, or disables, or reverts.
 *   4. The component pushes the full note shape up via onChange so the
 *      parent's job_groups state and the existing auto-save handler
 *      pick up the change with zero extra wiring.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  ChevronDown,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Undo2,
  AlertCircle,
  MessageSquareText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_TYPES = [
  "Repair Context",
  "Repair Risk / Limitation",
  "Monitoring Advice",
  "Cost-Saving Alternative",
  "Additional Workshop Finding",
  "Technician Recommendation",
  "Customer Information",
  "Temporary Repair Context",
  "Fabrication / Custom Repair",
  "Diagnostic Limitation",
  "Supplier / Parts Delay",
  "Previous Advisory Progression",
];

const REWRITE_MODES = [
  { value: "minimal", label: "Minimal Cleanup" },
  { value: "professional", label: "Professional Rewrite" },
  { value: "customer_friendly", label: "Customer-Friendly Explanation" },
];

const DEFAULT_NOTE = {
  note_type: "",
  raw_input: "",
  rewrite_mode: "professional",
  generated_text: "",
  final_text: "",
  enabled: false,
  generated_at: null,
  generated_by_id: null,
  model: null,
};

export default function ContextualCustomerNoteSection({
  yestimateId,
  jobGroup,
  jobIdx,
  onChange,
}) {
  const existing = jobGroup.contextual_customer_note || null;
  const initial = existing ? { ...DEFAULT_NOTE, ...existing } : { ...DEFAULT_NOTE };

  // Auto-expand when a note already exists; collapsed by default otherwise
  // so the pricing panel stays clean.
  const [expanded, setExpanded] = useState(!!existing && (existing.final_text || existing.raw_input));
  const [note, setNote] = useState(initial);
  const [generating, setGenerating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");
  const [needsMoreDetail, setNeedsMoreDetail] = useState(false);

  // Iter180f — Context-aware CCN preset chips. Fetched lazily when the
  // FOH expands the section so the API isn't called for every JG on
  // page-load. Presets are filtered server-side by vi_item_key, MOT
  // regex, MCC category etc. — so an oil-leak JG sees leak presets
  // first, brake JG sees brake presets, every JG sees the universal
  // monitor / workshop-finding tail.
  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);

  useEffect(() => {
    if (!expanded || presetsLoaded || !yestimateId || !jobGroup?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/ccn-presets`,
        );
        if (!cancelled) {
          setPresets(r.data?.presets || []);
          setPresetsLoaded(true);
        }
      } catch {
        // Non-blocking — presets are nice-to-have, the textarea still works.
        if (!cancelled) setPresetsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, presetsLoaded, yestimateId, jobGroup?.id]);

  const applyPreset = (preset) => {
    // Replace the raw_input with the preset text and stamp the note
    // type from the preset. Keeps things idempotent — clicking another
    // chip swaps the content cleanly rather than appending.
    pushUp({
      ...note,
      raw_input: preset.text,
      note_type: preset.note_type || note.note_type,
    });
  };

  // iter197l — Aide-Mémoir pill templates (context-aware quick-fill).
  // Two affordances per pill:
  //   • One-tap "Apply" → drops ready-to-ship customer text directly
  //     onto the JG's customer_notes via the apply endpoint. No AI
  //     rewrite, no Generate step needed.
  //   • "Customise" → drops the template's terse raw_input + note_type
  //     into the CCN editor for the AI-rewrite path.
  // Pills surface relevant ones first (shock-absorber JG sees shock
  // pills), then "always" generics (Previous Advisory, Parts Delay…).
  const [aidePills, setAidePills] = useState([]);
  const [aideLoaded, setAideLoaded] = useState(false);
  const [aideApplying, setAideApplying] = useState(null);
  const [aideAppliedMap, setAideAppliedMap] = useState({});

  useEffect(() => {
    if (!expanded || aideLoaded || !yestimateId || !jobGroup?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/aide-memoir-suggestions`,
        );
        if (!cancelled) {
          const pills = r.data?.pills || [];
          setAidePills(pills);
          // Seed the applied-state map from server-detected "already_applied".
          const seeded = {};
          pills.forEach((p) => { if (p.already_applied) seeded[p.key] = true; });
          setAideAppliedMap(seeded);
          setAideLoaded(true);
        }
      } catch {
        if (!cancelled) setAideLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, aideLoaded, yestimateId, jobGroup?.id]);

  const applyAideOneTap = async (pill) => {
    setAideApplying(pill.key);
    // iter211 — FOH feedback: clicking a quick-add pill MUST also
    // populate the raw note textarea so the FOH can see what was
    // added, edit it, and hit Generate Customer Note. Previously the
    // pill silently posted to /aide-memoir/apply and left the textarea
    // empty, which surfaced as "Please enter the raw note before
    // generating." the next time FOH pressed Generate.
    const existing = (note.raw_input || "").trimEnd();
    const addition = (pill.raw_input || pill.customer_text || "").trim();
    if (addition) {
      const already = existing
        && existing.toLowerCase().includes(addition.toLowerCase().slice(0, 30));
      if (!already) {
        const combined = existing ? `${existing}\n${addition}` : addition;
        pushUp({
          ...note,
          raw_input: combined,
          note_type: note.note_type || pill.note_type || "Repair Context",
        });
      }
    }
    try {
      const r = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/aide-memoir/${pill.key}/apply`,
      );
      // Mark as applied locally — server-side persistence is done.
      setAideAppliedMap((m) => ({ ...m, [pill.key]: true }));
      // Push the new customer_notes up through onChange so the rest of
      // the pricing UI (and the live yestimate state) re-renders.
      onChange?.({
        ...note,
        raw_input: addition && !(existing
          && existing.toLowerCase().includes(addition.toLowerCase().slice(0, 30)))
          ? (existing ? `${existing}\n${addition}` : addition)
          : note.raw_input,
        note_type: note.note_type || pill.note_type || "Repair Context",
        _customer_notes_override: r.data?.customer_notes || [],
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to apply pill.");
    } finally {
      setAideApplying(null);
    }
  };

  const customiseAide = (pill) => {
    // iter197l — Append to the existing raw_input rather than replace
    // so FOH can stack 2-3 shorthand pills into one AI-polished
    // paragraph. The note_type follows the FIRST pill clicked (changing
    // it part-way through would surprise the rewrite). Existing manual
    // text is preserved at the top.
    const existing = (note.raw_input || "").trimEnd();
    const addition = (pill.raw_input || pill.customer_text || "").trim();
    if (!addition) return;
    // Don't duplicate if the same pill's shorthand is already present.
    if (existing && existing.toLowerCase().includes(addition.toLowerCase().slice(0, 30))) {
      return;
    }
    const combined = existing
      ? `${existing}\n${addition}`
      : addition;
    pushUp({
      ...note,
      raw_input: combined,
      // Only stamp note_type on the first pill — keep FOH's manual
      // pick if they had set one before.
      note_type: note.note_type || pill.note_type || "Repair Context",
    });
  };

  const pushUp = (next) => {
    setNote(next);
    onChange?.(next);
  };

  const handleGenerate = async () => {
    setError("");
    setNeedsMoreDetail(false);
    if (!note.raw_input.trim()) {
      setError("Please enter the raw note before generating.");
      return;
    }
    if (!note.note_type) {
      setError("Please select a note type before generating.");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/job-group/${jobGroup.id}/contextual-note/generate`,
        {
          note_type: note.note_type,
          raw_input: note.raw_input,
          rewrite_mode: note.rewrite_mode,
        }
      );
      const data = res.data || {};
      if (data.needs_more_detail) {
        setNeedsMoreDetail(true);
        return;
      }
      const next = {
        ...note,
        generated_text: data.generated_text || "",
        // First generation auto-fills the editable final text. Subsequent
        // regenerations leave final_text alone if FOH has manually edited.
        final_text: editMode && note.final_text ? note.final_text : (data.generated_text || ""),
        generated_at: data.generated_at,
        generated_by_id: data.generated_by_id,
        model: data.model,
        // Stamp on the note so we can surface a small "grounded in tester
        // MCC evidence" hint to FOH (and so it persists across reloads).
        mcc_grounded: !!data.mcc_grounded,
      };
      pushUp(next);
    } catch (e) {
      setError(e?.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevert = () => {
    pushUp({ ...note, final_text: note.generated_text || "" });
    setEditMode(false);
  };

  const handleClear = () => {
    pushUp({ ...DEFAULT_NOTE });
    setEditMode(false);
    setError("");
    setNeedsMoreDetail(false);
  };

  const toggleEnabled = (val) => {
    pushUp({ ...note, enabled: val });
  };

  const hasPreview = !!(note.generated_text && note.generated_text.trim());

  return (
    <Card
      className="mt-3 border-slate-200 bg-slate-50/60"
      data-testid={`contextual-note-section-${jobIdx}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-100/80 transition-colors rounded-t-lg"
        data-testid={`contextual-note-toggle-${jobIdx}`}
      >
        <div className="flex items-center gap-2">
          <MessageSquareText className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
              Contextual Customer Note
              {note.enabled && note.final_text && (
                <span
                  className="text-[10px] font-medium normal-case tracking-normal text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded"
                  data-testid={`contextual-note-active-pill-${jobIdx}`}
                >
                  Active
                </span>
              )}
              {note.final_text && !note.enabled && (
                <span className="text-[10px] font-medium normal-case tracking-normal text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded">
                  Draft saved · off
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 italic">
              Optional customer-facing contextual guidance for unusual repairs,
              limitations, or workshop recommendations.
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2.5" data-testid={`contextual-note-body-${jobIdx}`}>
          {/* iter197z2 light-tidy — Combined "Quick adds" row. Merges
              the old separate "Aide-mémoir" + "Quick presets" rows
              into a single chip row so FOH sees fewer labels. Pill
              styling preserves the visual distinction (amber for
              aide-mémoir with optional one-tap apply + ✎ customise,
              indigo for quick presets that drop straight into the raw
              note). Both behaviours unchanged — only the layout is
              tightened. */}
          {(aidePills.length > 0 || presets.length > 0) && (
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-emerald-600" />
                Quick adds
                <span className="text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                  · context-aware shortcuts
                </span>
              </Label>
              <div
                className="flex flex-wrap gap-1.5"
                data-testid={`quick-adds-row-${jobIdx}`}
              >
                {/* Aide-mémoir pills first (one-tap apply + customise) */}
                {aidePills.map((p) => {
                  const applied = aideAppliedMap[p.key];
                  const applying = aideApplying === p.key;
                  return (
                    <div key={`aide-${p.key}`} className="inline-flex rounded-full overflow-hidden border-2 transition-colors"
                      style={{
                        borderColor: applied ? "#a7f3d0" : "#fde68a",
                        background:  applied ? "#ecfdf5" : "#fffbeb",
                      }}
                      data-testid={`aide-memoir-pill-row-${jobIdx}-${p.key}`}
                    >
                      <button
                        type="button"
                        onClick={() => !applied && !applying && applyAideOneTap(p)}
                        disabled={applied || applying}
                        className="px-2.5 py-1 text-[11px] font-medium max-w-[260px] truncate
                                   hover:bg-amber-100 disabled:hover:bg-transparent
                                   disabled:cursor-default text-amber-900"
                        title={p.customer_text}
                        data-testid={`aide-memoir-pill-${p.key}-${jobIdx}`}
                      >
                        {applied ? "✓ " : applying ? "… " : ""}{p.label}
                      </button>
                      {!applied && (
                        <button
                          type="button"
                          onClick={() => customiseAide(p)}
                          disabled={applying}
                          className="px-1.5 py-1 text-[11px] border-l-2 border-amber-200
                                     text-amber-700 hover:bg-amber-100"
                          title="Customise with AI rewrite — drops the shorthand into the editor"
                          data-testid={`aide-memoir-customise-${p.key}-${jobIdx}`}
                        >
                          ✎
                        </button>
                      )}
                    </div>
                  );
                })}
                {/* Quick presets (indigo — drop straight into raw note) */}
                {presets.map((p) => (
                  <button
                    key={`preset-${p.id}`}
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="px-2.5 py-1 rounded-full text-[11px] font-medium border
                               border-indigo-200 bg-indigo-50 text-indigo-800
                               hover:bg-indigo-100 hover:border-indigo-300
                               transition-colors max-w-full truncate"
                    title={p.text}
                    data-testid={`contextual-note-preset-${p.id}-${jobIdx}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Raw FOH note with inline compact selectors above */}
          <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-[11px] font-semibold text-slate-600">
                Raw note <span className="text-slate-400 font-normal">(shorthand is fine)</span>
              </Label>
              {/* iter197z2 light-tidy — Note type + Rewrite mode
                  shrunk from full-width grid cells to a compact inline
                  selector row above the textarea. Saves ~60px of
                  vertical space and visually de-prioritises the
                  settings vs the textarea (which is what the FOH
                  actually edits). */}
              <div className="flex items-center gap-1.5">
                <Select
                  value={note.note_type || ""}
                  onValueChange={(v) => pushUp({ ...note, note_type: v })}
                >
                  <SelectTrigger
                    className="h-7 text-[11px] bg-white max-w-[170px] px-2"
                    data-testid={`contextual-note-type-select-${jobIdx}`}
                  >
                    <SelectValue placeholder="Note type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={note.rewrite_mode || "professional"}
                  onValueChange={(v) => pushUp({ ...note, rewrite_mode: v })}
                >
                  <SelectTrigger
                    className="h-7 text-[11px] bg-white max-w-[150px] px-2"
                    data-testid={`contextual-note-mode-select-${jobIdx}`}
                    title="AI rewrite mode"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REWRITE_MODES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea
              value={note.raw_input}
              onChange={(e) => pushUp({ ...note, raw_input: e.target.value })}
              placeholder="e.g. Technician advises original retaining bracket missing. Can manufacture custom securing bracket instead of replacing complete tray assembly."
              className="text-xs bg-white min-h-[60px]"
              data-testid={`contextual-note-raw-input-${jobIdx}`}
            />
          </div>

          {/* Generate / status row */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
              data-testid={`contextual-note-generate-btn-${jobIdx}`}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3 mr-1.5" />
                  {hasPreview ? "Regenerate" : "Generate Customer Note"}
                </>
              )}
            </Button>
            {hasPreview && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClear}
                className="h-8 text-xs text-slate-500"
                data-testid={`contextual-note-clear-btn-${jobIdx}`}
              >
                Clear
              </Button>
            )}
            {error && (
              <span className="flex items-center gap-1 text-[11px] text-red-600">
                <AlertCircle className="w-3 h-3" /> {error}
              </span>
            )}
            {needsMoreDetail && (
              <span className="flex items-center gap-1 text-[11px] text-amber-700">
                <AlertCircle className="w-3 h-3" />
                Add a bit more detail and try again.
              </span>
            )}
          </div>

          {/* Side-by-side preview */}
          {hasPreview && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1"
              data-testid={`contextual-note-preview-${jobIdx}`}
            >
              <div className="bg-white border border-slate-200 rounded p-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                  Raw FOH note
                </div>
                <p className="text-[12px] text-slate-700 whitespace-pre-wrap">
                  {note.raw_input}
                </p>
              </div>
              <div className="bg-white border border-indigo-200 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase tracking-wide text-indigo-600 font-semibold flex items-center gap-1.5">
                    Customer-facing note
                    {note.mcc_grounded && (
                      <span
                        className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded"
                        title="Grounded in the tester's MCC evidence (severity, observed / suspected / confirmed, recommendation)."
                        data-testid={`contextual-note-mcc-grounded-pill-${jobIdx}`}
                      >
                        MCC-grounded
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setEditMode((m) => !m)}
                      className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                      data-testid={`contextual-note-edit-toggle-${jobIdx}`}
                    >
                      {editMode ? "Done" : "Edit"}
                    </button>
                    {editMode && note.generated_text && note.final_text !== note.generated_text && (
                      <button
                        type="button"
                        onClick={handleRevert}
                        className="flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-slate-700"
                        data-testid={`contextual-note-revert-btn-${jobIdx}`}
                      >
                        <Undo2 className="w-3 h-3" /> Revert
                      </button>
                    )}
                  </div>
                </div>
                {editMode ? (
                  <Textarea
                    value={note.final_text}
                    onChange={(e) => pushUp({ ...note, final_text: e.target.value })}
                    className="text-[12px] min-h-[80px]"
                    data-testid={`contextual-note-final-text-input-${jobIdx}`}
                  />
                ) : (
                  <p
                    className="text-[12px] text-slate-800 whitespace-pre-wrap"
                    data-testid={`contextual-note-final-text-${jobIdx}`}
                  >
                    {note.final_text || note.generated_text}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Enable / disable for portal display */}
          {hasPreview && (
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-slate-700">
                  Show on customer portal
                </span>
                <span className="text-[11px] text-slate-500">
                  When enabled, the customer-facing note appears under the
                  item explanation as “Advisor Contextual Note”.
                </span>
              </div>
              <Switch
                checked={!!note.enabled}
                onCheckedChange={toggleEnabled}
                data-testid={`contextual-note-enabled-switch-${jobIdx}`}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
